import { StaticModal, createModal } from '@nexelem/modals';
import { useCustomTranslation } from '../../../../../hooks';
import { Button } from '@nexelem/components';
import { FormDateTimeInput } from '@nexelem/form-components';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSaveSimulationPlanningBoundaryMutation, useSimulationId } from '../hooks';
import { Trans } from 'react-i18next';
import { SimulationMetadataView } from '@nexelem/graphne-api';

const toPublishRangeSchema = z.object({
  dateTime: z.date(),
});

type ToPublishRangeFormValues = z.infer<typeof toPublishRangeSchema>;

type ToPublishRangeModalProps = {
  simulationMeta: SimulationMetadataView;
};

const ToPublishRangeModal = ({ simulationMeta }: ToPublishRangeModalProps) => {
  const { t, tWrapper } = useCustomTranslation('products.productionPlan');
  const { mutate, isPending } = useSaveSimulationPlanningBoundaryMutation();
  const { id } = useSimulationId();

  const methods = useForm({
    resolver: zodResolver(toPublishRangeSchema),
    defaultValues: {
      dateTime: new Date(simulationMeta.planningBoundary),
    },
  });

  const onSubmit = (data: ToPublishRangeFormValues) => {
    mutate(
      {
        simulationId: id,
        planningBoundary: data.dateTime.toISOString(),
      },
      {
        onSuccess: () => {
          closeToPublishRangeModal();
        },
      },
    );
  };

  return (
    <FormProvider {...methods}>
      <StaticModal
        component="form"
        maxWidth="lg"
        onClose={closeToPublishRangeModal}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <StaticModal.Header withCloseButton>{tWrapper('toPublishRange')}</StaticModal.Header>
        <StaticModal.Content className="scroll-stable pr-2">
          <div className="mb-8 text-s18">
            <Trans components={{ b: <b /> }}>{tWrapper('selectPlanningRangeDateTime')}</Trans>
          </div>
          <FormDateTimeInput required name="dateTime" />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeToPublishRangeModal}>
            {t('common.cancel')}
          </Button>
          <Button isLoading={isPending} type="submit" variant="contained">
            {t('common.save')}
          </Button>
        </StaticModal.Footer>
      </StaticModal>
    </FormProvider>
  );
};

export const { openToPublishRangeModal, closeToPublishRangeModal } = createModal(ToPublishRangeModal, {
  name: 'ToPublishRangeModal',
});
